/* eslint no-shadow:0, no-param-reassign:0 */
import {
  clearReferralAgentCode,
  getReferralAgentCode,
  persistReferralAgentCode,
  persistReferralSnapshot,
  readReferralSnapshot,
  clearReferralSnapshot,
  validateReferralCode,
} from '@/services/referralApi';

const initialState = () => ({
  code: null,
  agent: null,
  status: 'idle', // idle | loading | valid | invalid
  source: null, // link | manual | null
  lastError: null,
});

const state = initialState();

const getters = {
  referralCode: (state) => state.code,
  referralAgent: (state) => state.agent,
  referralStatus: (state) => state.status,
  referralSource: (state) => state.source,
  hasValidReferral: (state) => state.status === 'valid' && !!state.agent,
  /**
   * Link referrals stay locked while revalidating so the picker never flashes Direct
   * after product navigation / tab focus.
   */
  isReferralLocked: (state) => (
    state.source === 'link'
    && !!state.agent
    && (state.status === 'valid' || state.status === 'loading')
  ),
  isReferralLoading: (state) => state.status === 'loading',
  referralAgencyName: (state) => (state.agent?.agencyName ? String(state.agent.agencyName) : ''),
};

const mutations = {
  SET_LOADING(state) {
    state.status = 'loading';
    state.lastError = null;
  },
  SET_VALID(state, { code, agent, source }) {
    state.code = code;
    state.agent = agent;
    state.status = 'valid';
    state.source = source || 'link';
    state.lastError = null;
  },
  SET_INVALID(state, { code = null, error = null } = {}) {
    state.code = code;
    state.agent = null;
    state.status = 'invalid';
    state.source = null;
    state.lastError = error;
  },
  CLEAR(state) {
    Object.assign(state, initialState());
  },
};

function persistLinkReferral(code, agent) {
  const trimmed = String(code || '').trim();
  if (!trimmed || !agent) return;
  persistReferralAgentCode(trimmed);
  persistReferralSnapshot({ code: trimmed, agent, source: 'link' });
}

const actions = {
  /**
   * Restore last known link referral from localStorage before async validation.
   * Keeps the picker locked across reloads / product navigations / new tabs.
   */
  hydrateFromStorage({ state, commit }) {
    if (state.agent && (state.status === 'valid' || state.status === 'loading')) return;

    const snapshot = readReferralSnapshot();
    if (!snapshot?.agent || !snapshot?.code) return;
    // Only auto-restore link referrals; manual picks are session-scoped.
    if (snapshot.source === 'manual') return;

    commit('SET_VALID', {
      code: snapshot.code,
      agent: snapshot.agent,
      source: 'link',
    });
    persistReferralAgentCode(snapshot.code);
  },

  clearReferral({ commit }) {
    clearReferralAgentCode();
    clearReferralSnapshot();
    commit('CLEAR');
  },

  /** Customer chose Direct — drop any linked/manual agent for this session. */
  selectDirect({ commit }) {
    clearReferralAgentCode();
    clearReferralSnapshot();
    commit('CLEAR');
  },

  /** Customer picked an agent from search. */
  selectManualAgent({ commit }, agent) {
    if (!agent) {
      clearReferralAgentCode();
      clearReferralSnapshot();
      commit('CLEAR');
      return;
    }
    const code = agent.referralCode || agent.accountNo || String(agent.agencyCode || '');
    // Manual pick is session-only; do not keep a link snapshot that would re-lock later.
    clearReferralSnapshot();
    if (code) persistReferralAgentCode(code);
    commit('SET_VALID', { code, agent, source: 'manual' });
  },

  /**
   * Capture optional query/cookie/snapshot code, validate against GIS.
   * Never clears an existing link referral just because the current URL has no agentCode
   * (product trays previously stripped query params).
   */
  async ensureReferralValidated({ state, commit, dispatch }, routeQuery) {
    await dispatch('hydrateFromStorage');

    const code = getReferralAgentCode(routeQuery);
    if (!code) {
      if (state.source === 'manual' && state.status === 'valid' && state.agent) {
        return state.agent;
      }
      if (state.source === 'link' && state.agent) {
        return state.agent;
      }
      if (state.status !== 'idle' || state.agent) commit('CLEAR');
      return null;
    }

    if (state.status === 'valid' && state.code === code && state.agent) {
      if (state.source === 'link') {
        persistLinkReferral(code, state.agent);
      } else {
        persistReferralAgentCode(code);
      }
      return state.agent;
    }

    const previousLinkAgent = state.source === 'link' && state.agent ? state.agent : null;
    const previousCode = state.code;

    commit('SET_LOADING');
    const agent = await validateReferralCode(code);
    if (!agent) {
      // Transient GIS/auth failure: keep hydrated link agent rather than dropping to Direct.
      if (previousLinkAgent && (!previousCode || previousCode === code)) {
        persistLinkReferral(code, previousLinkAgent);
        commit('SET_VALID', { code, agent: previousLinkAgent, source: 'link' });
        return previousLinkAgent;
      }
      // Keep cookie so we can retry after login (GIS may require auth).
      persistReferralAgentCode(code);
      commit('SET_INVALID', { code, error: 'Invalid or inactive referral code' });
      return null;
    }

    const resolvedCode = agent.referralCode || agent.accountNo || code;
    persistLinkReferral(resolvedCode, agent);
    commit('SET_VALID', { code: resolvedCode, agent, source: 'link' });
    return agent;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
